import { Controller } from "stimulus"

export default class extends Controller {
    static targets = ["input", "label", "wrapper"]

    connect() {
        // Listen for form submission validation failures (using capture because 'invalid' doesn't bubble)
        this._invalidHandler = this.handleInvalid.bind(this)
        this.element.addEventListener('invalid', this._invalidHandler, true)

        // Find the input element and attach event listeners directly
        // (blur doesn't bubble, so we need to attach to the input itself)
        setTimeout(() => {
            this.input = this.hasInputTarget ? this.inputTarget : this.element.querySelector('input, textarea, select')

            if (this.input) {
                this._blurHandler = this.handleBlur.bind(this)
                this._inputHandler = this.handleInput.bind(this)
                this._keyupHandler = this.handleInput.bind(this)

                this.input.addEventListener('blur', this._blurHandler)
                this.input.addEventListener('input', this._inputHandler)
                this.input.addEventListener('keyup', this._keyupHandler)
            }

            // Monitor form submission
            this.form = this.element.closest('form')
            if (this.form) {
                this._submitHandler = () => {
                    // Short delay to let browser validation happen first
                    setTimeout(() => this.validate(), 10)
                }
                this.form.addEventListener('submit', this._submitHandler)

                // Also catch redundant submit attempts (e.g. if browser blocks submit)
                const submitBtn = this.form.querySelector('[type="submit"]')
                if (submitBtn) {
                    this._clickHandler = () => setTimeout(() => this.validate(), 50)
                    submitBtn.addEventListener('click', this._clickHandler)
                }
            }
        }, 100)
    }

    disconnect() {
        if (this._invalidHandler) {
            this.element.removeEventListener('invalid', this._invalidHandler, true)
        }
        if (this.input) {
            if (this._blurHandler) this.input.removeEventListener('blur', this._blurHandler)
            if (this._inputHandler) this.input.removeEventListener('input', this._inputHandler)
            if (this._keyupHandler) this.input.removeEventListener('keyup', this._keyupHandler)
        }
        if (this.form) {
            if (this._submitHandler) this.form.removeEventListener('submit', this._submitHandler)
            const submitBtn = this.form.querySelector('[type="submit"]')
            if (submitBtn && this._clickHandler) submitBtn.removeEventListener('click', this._clickHandler)
        }
    }

    handleInvalid(event) {
        console.log('Validation failed for:', event.target.name || event.target.id)
        this.element.classList.add('is-invalid')
        this.showErrorMessage(event.target)
    }

    getErrorMessage(input) {
        const fieldName = input.name || input.id || ''
        const value = input.value ? input.value.trim() : ''

        // Check for required field empty
        if (input.hasAttribute('required') && value === '') {
            // Check if mobile (sm breakpoint) and weight/height field
            // Use window.innerWidth < 640 for mobile check (standard tailwind sm)
            if (window.innerWidth < 640 && /weight|height/i.test(fieldName)) {
                return null
            }
            return 'This field is required'
        }

        // Check for pattern mismatch
        if (input.validity.patternMismatch) {
            // Custom messages based on field name
            if (fieldName.includes('postal_code') || fieldName.includes('zip')) {
                return 'ZIP / Postal Code must be 2-12 characters (letters, numbers, spaces, or hyphens)'
            }
            if (fieldName.includes('cell_phone') || fieldName.includes('phone')) {
                return 'Please enter a valid phone number'
            }
            if (fieldName.includes('sag_number')) {
                return 'SAG Number must be 7-12 alphanumeric characters'
            }
            if (fieldName.includes('hst')) {
                return 'HST Number must be maximum 15 alphanumeric characters'
            }
            if (fieldName.includes('federal_tax_id') || fieldName.includes('gst')) {
                const isGST = this.element.innerText.includes('GST')
                if (isGST) {
                    return 'GST Number must be maximum 15 alphanumeric characters'
                } else {
                    return 'Federal Tax ID must be in the format XX-XXXXXXX'
                }
            }
            return 'Please enter a valid format'
        }

        // Check for length issues
        if (input.validity.tooShort) {
            return `Minimum ${input.minLength} characters required`
        }
        if (input.validity.tooLong) {
            return `Maximum ${input.maxLength} characters allowed`
        }

        // Check for type mismatch (email, url, etc.)
        if (input.validity.typeMismatch) {
            if (input.type === 'email') {
                return 'Please enter a valid email address'
            }
            return 'Please enter a valid value'
        }

        // Default message
        return 'Please enter a valid value'
    }

    showErrorMessage(input) {
        // Remove any existing error message first
        this.removeErrorMessage()

        const errorMessage = this.getErrorMessage(input)
        // Return early if no error message to display (e.g. for mobile weight/height)
        if (!errorMessage) return

        // Return early if no error message to display (e.g. for mobile weight/height)
        if (!errorMessage) return

        // Create error message element
        const errorDiv = document.createElement('div')
        errorDiv.className = 'client-validation-error px-2 mt-1'
        errorDiv.innerHTML = `<span class="text-xs text-red-600">${errorMessage}</span>`

        // Find the appropriate container to append to
        const container = this.element.querySelector('.flex.flex-col.items-start') || this.element
        container.appendChild(errorDiv)
    }

    removeErrorMessage() {
        const existingError = this.element.querySelector('.client-validation-error')
        if (existingError) {
            existingError.remove()
        }
    }

    validate() {
        const input = this.input || this.element.querySelector('input, textarea, select')
        if (!input) {
            console.warn('No input found for validation controller', this.element)
            return
        }

        const value = input.value ? input.value.trim() : ""

        // Skip validation if field is empty and not required
        if (value === "" && !input.hasAttribute('required')) {
            this.element.classList.remove('is-invalid')
            this.removeErrorMessage()
            return
        }

        const isValid = input.checkValidity()

        // Consider it invalid if browser says so OR if it's required and empty
        const isInvalid = !isValid || (input.hasAttribute('required') && value === "")

        if (isInvalid) {
            this.element.classList.add('is-invalid')
            this.showErrorMessage(input)
        } else {
            this.element.classList.remove('is-invalid')
            this.removeErrorMessage()
        }
    }

    handleBlur() {
        this.validate()
    }

    handleInput() {
        // Real-time validation as user types
        this.validateRealtime()
    }

    validateRealtime() {
        const input = this.input || this.element.querySelector('input, textarea, select')
        if (!input) return

        const value = input.value || ""
        const fieldName = input.name || input.id || ''

        // For postal_code field - validate in real-time
        if (fieldName.includes('postal_code') || fieldName.includes('zip')) {
            const pattern = /^[A-Za-z0-9\s\-]{2,12}$/

            if (value === "") {
                // Empty field - show required error if required
                if (input.hasAttribute('required')) {
                    this.element.classList.add('is-invalid')
                    this.showCustomError('This field is required')
                } else {
                    this.element.classList.remove('is-invalid')
                    this.removeErrorMessage()
                }
            } else if (value.length < 2) {
                this.element.classList.add('is-invalid')
                this.showCustomError('ZIP / Postal Code must be at least 2 characters')
            } else if (value.length > 12) {
                this.element.classList.add('is-invalid')
                this.showCustomError('ZIP / Postal Code must be at most 12 characters')
            } else if (!pattern.test(value)) {
                this.element.classList.add('is-invalid')
                this.showCustomError('Only letters, numbers, spaces, and hyphens allowed')
            } else {
                // Valid
                this.element.classList.remove('is-invalid')
                this.removeErrorMessage()
            }
            return
        }

        // For SAG Number
        if (fieldName.includes('sag_number')) {
            const pattern = /^[A-Za-z0-9]{7,12}$/
            if (value !== "" && !pattern.test(value)) {
                this.element.classList.add('is-invalid')
                if (value.length < 7) {
                    this.showCustomError('Minimum 7 characters required')
                } else if (value.length > 12) {
                    this.showCustomError('Maximum 12 characters allowed')
                } else {
                    this.showCustomError('Only letters and numbers allowed')
                }
                return
            } else if (value !== "") {
                this.element.classList.remove('is-invalid')
                this.removeErrorMessage()
            }
        }

        // For GST / HST Number
        if (fieldName.includes('hst') || fieldName.includes('federal_tax_id')) {
            // Check if it's actually the GST field (labeled GST) for Canadian users
            const isGSTorHST = fieldName.includes('hst') || 
                             (this.element.innerText.includes('GST Number') || this.element.innerText.includes('HST Number'))
            
            if (isGSTorHST) {
                const pattern = /^[A-Za-z0-9]{1,15}$/
                if (value !== "" && !pattern.test(value)) {
                    this.element.classList.add('is-invalid')
                    if (value.length > 15) {
                        this.showCustomError('Maximum 15 characters allowed')
                    } else {
                        this.showCustomError('Only letters and numbers allowed')
                    }
                    return
                } else if (value !== "") {
                    this.element.classList.remove('is-invalid')
                    this.removeErrorMessage()
                }
            }
        }

        // For other fields, use standard validation
        this.validate()
    }

    showCustomError(message) {
        this.removeErrorMessage()

        const errorDiv = document.createElement('div')
        errorDiv.className = 'client-validation-error px-2 mt-1'
        errorDiv.innerHTML = `<span class="text-xs text-red-600">${message}</span>`

        const container = this.element.querySelector('.flex.flex-col.items-start') || this.element
        container.appendChild(errorDiv)
    }
}
